import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { NavigationCancel, NavigationCancellationCode, NavigationEnd, NavigationError, Router, RouterModule } from "@angular/router";
import { AuthModule, OidcSecurityService } from "angular-auth-oidc-client";

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { fromEvent } from 'rxjs';
import { DownloadIndicatorComponent } from './shared/download-indicator/download-indicator.component';
import { LoadingScreenComponent } from './shared/loading-screen/loading-screen.component';
import { RedirectScreenComponent } from './shared/redirect-screen/redirect-screen.component';
import { SiteSearchComponent } from './shared/site-search/site-search.component';
import { InstallationSelectors } from './state/installations-state/installation-state.selectors';
import { GeneralStateAction } from './state/pageState/page-state.actions';



@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RouterModule, AuthModule, SiteSearchComponent, RedirectScreenComponent, DownloadIndicatorComponent, LoadingScreenComponent]
})

export class AppComponent implements OnInit {
  store: Store = inject(Store);
  destroyRef: DestroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly oidc = inject(OidcSecurityService);

  /**
   * Until the first navigation ends, the router outlet is empty while the route guards wait on the
   * latest installations, the installation details and the permissions. Opening the app with a
   * session already in place (not through /signin-oidc) would otherwise show only the page
   * background, so the loading screen covers that wait too.
   */
  protected readonly firstNavigationDone = signal(false);
  private readonly navigationErrored = signal(false);
  private readonly startupLoadFailed = this.store.selectSignal(InstallationSelectors.selectStartupLoadFailed);
  /**
   * Signed out, the browser is on its way to the sign-in page (AuthenticationFactory, PermissionGuard) and the
   * requests failed only for want of a token: that is still loading, not failed, and not access denied.
   */
  private readonly signedIn = computed(() => this.oidc.authenticated().isAuthenticated);
  protected readonly startupFailed = computed(() =>
    this.signedIn() && (this.startupLoadFailed() || this.navigationErrored()));

  /**
   * Only a safety net here: real failures arrive through startupFailed, and a direct load waits on sign-in,
   * the installation, its details and its permissions one after the other - more than 8 seconds on a slow
   * line, which would offer Reload while the load is still on its way.
   */
  protected readonly stuckAfterMs = 30000;

  constructor() {
    fromEvent(window, 'pageshow')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // Reset the show redirectScreen every time the page is loaded.
        this.store.dispatch(
          GeneralStateAction.setShowRedirectScreenState({ showRedirectScreen: false })
        );
      }
      );

    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        if (event instanceof NavigationEnd) this.firstNavigationDone.set(true);
        if (event instanceof NavigationError && !this.firstNavigationDone()) this.navigationErrored.set(true);
        // A guard turned the first navigation down. Because a request failed: the loading screen already says
        // so (startupFailed). Otherwise the user may not open that page - no access, which is not an outage -
        // so the access denied page, not "Failed to load" with a Reload that only repeats it.
        if (event instanceof NavigationCancel && event.code === NavigationCancellationCode.GuardRejected
          && !this.firstNavigationDone() && this.signedIn() && !this.startupFailed()) {
          this.router.navigateByUrl('/access-denied');
        }
      });
  }
  ngOnInit() {
  }
}
